'use client';

import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@routly/ui';
import { Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { Building2, Car, MapPin, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
} from '@/components/shared';
import { useOrganizations } from '@/hooks/useOrganizations';
import { CreateOrganizationDialog } from './CreateOrganizationDialog';

export function OrganizationsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data, isLoading, error } = useOrganizations({
    page,
    limit: 20,
    search: search || undefined,
  });

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState title="Error loading organizations" message={error.message} />;
  }

  const organizations = data?.data || [];
  const pagination = data?.pagination;

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title="Organizations"
        description="Manage client organizations and their settings"
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Organization
          </Button>
        }
      />

      <div className="flex gap-4">
        <SearchInput placeholder="Search organizations..." value={search} onChange={handleSearch} />
      </div>

      {organizations.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No organizations yet"
          description={
            search
              ? 'No organizations match your search criteria.'
              : 'Organizations will appear here once you create them.'
          }
          action={
            !search && (
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Organization
              </Button>
            )
          }
        />
      ) : (
        <Card>
          <OrganizationsTable organizations={organizations} />
          {pagination && (
            <Pagination
              pagination={pagination}
              currentPage={page}
              onPageChange={setPage}
              itemName="organizations"
            />
          )}
        </Card>
      )}

      <CreateOrganizationDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} />
    </>
  );
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  createdAt: string;
  partner?: { id: string; name: string; slug: string } | null;
  stats?: {
    users?: number;
    vehicles?: number;
    geofences?: number;
  };
}

function OrganizationsTable({ organizations }: { organizations: Organization[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Organization</TableHead>
          <TableHead>Partner</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Resources</TableHead>
          <TableHead>Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {organizations.map((org) => (
          <TableRow key={org.id} className="hover:bg-muted/50">
            <TableCell>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <Link
                    to="/organizations/$organizationId"
                    params={{ organizationId: org.id }}
                    className="font-medium hover:underline"
                  >
                    {org.name}
                  </Link>
                  <p className="text-sm text-muted-foreground">{org.slug}</p>
                </div>
              </div>
            </TableCell>
            <TableCell>
              {org.partner ? (
                <Link
                  to="/partners/$partnerId"
                  params={{ partnerId: org.partner.id }}
                  className="text-sm hover:underline"
                >
                  {org.partner.name}
                </Link>
              ) : (
                <span className="text-sm text-muted-foreground">Direct</span>
              )}
            </TableCell>
            <TableCell>
              <Badge variant={org.isActive ? 'success' : 'secondary'}>
                {org.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {org.stats?.users || 0}
                </span>
                <span className="flex items-center gap-1">
                  <Car className="h-4 w-4" />
                  {org.stats?.vehicles || 0}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {org.stats?.geofences || 0}
                </span>
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {formatDistanceToNow(new Date(org.createdAt), { addSuffix: true })}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
